import React from 'react';
import { createSelector } from 'reselect';
import get from 'lodash/get';

import {
  dataSelector,
  metricSelector,
  filtersTableDataSortSelector,
} from '@store/noc/filtersData/selectors';
import { initThresholdSelector, isThresholdDisabledSelector } from '@store/noc/threshold/selectors';
import { autoSortEnabledSelector } from '@store/noc/base/selectors';
import { getMetricMetaByKey } from '@src/utils/metrics/metricHelpers';
import { Cell, Scope, Header } from '../common';
import { thresholdBaselineSelector, calcRowThreshold, autoSort } from '../common/utils';
import { ScopeWrapper } from '../styled';
import { currentMetricsMetaSelector } from '@store/common/selectors';

export const columnsSelector = createSelector(
  dataSelector,
  metricSelector,
  currentMetricsMetaSelector,
  (res, metrics, allMetircs) => {
    const { maxData } = res;

    const columns: Record<string, any>[] = [
      {
        title: '',
        dataKey: 'filter',
        key: 'filter.name',
        width: 400,
        frozen: true,
        resizable: true,
        cellRenderer: ({ cellData, rowData }) => (
          <ScopeWrapper>
            <Scope text={cellData.name} rowData={rowData} />
          </ScopeWrapper>
        ),
        align: 'left',
      },
    ];

    metrics.forEach((metric) => {
      if (allMetircs[metric]) {
        const metricInfo = getMetricMetaByKey({ metricKey: metric });
        const max = maxData[metric];
        const column = {
          title: metricInfo.short || metricInfo.displayName,
          dataKey: metric,
          key: metric,
          width: 180,
          style: { overflow: 'auto' },
          maxData: max,
          headerRenderer: () => <Header metric={metricInfo} />,
          cellRenderer: ({ cellData, column, rowData }) => (
            <Cell
              cellData={cellData}
              column={column}
              rowData={rowData}
              unit={metricInfo.unit}
              max={maxData[metric]}
            />
          ),
          align: 'left',
          sortable: true,
        };
        columns.push(column);
      }
    });

    return columns;
  },
);

// map threshold data for faster lookup
export const thresholdSelector = createSelector(initThresholdSelector, (res) => {
  const thresholdList = res.data.thresholds || [];
  const values = {};

  thresholdList.forEach((row) => {
    values[row.filter.fid || row.filter.key] = row;
  });
  return values;
});

export const warmingUpFiltersSelector = createSelector(dataSelector, (res) =>
  get(res, 'warmup', []).map((row) => ({
    id: row.fid || row.key,
    filter: { name: row.name },
    warmup: true,
  })),
);

// build data from response
export const tableDataSelector = createSelector(
  filtersTableDataSortSelector,
  thresholdSelector,
  thresholdBaselineSelector,
  metricSelector,
  isThresholdDisabledSelector,
  warmingUpFiltersSelector,
  autoSortEnabledSelector,
  (
    res,
    thresholds,
    thresholdBaseline,
    metrics,
    isThresholdDisabled,
    warmupFilters,
    isAutoSortEnabled,
  ) => {
    const { data, sort } = res;

    const result =
      data.map((rowData) => ({
        ...rowData,
        // calculate row level threshold
        threshold: isThresholdDisabled
          ? {}
          : calcRowThreshold({
              rowData,
              metrics,
              thresholds,
              thresholdBaseline,
            }),
      })) || [];

    autoSort(isAutoSortEnabled, result);

    return {
      data: result.concat(warmupFilters),
      sort,
    };
  },
);
